import React from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { styled } from '@uhg-abyss/web/tools/styled';
import { createColumn, useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { useAuth } from '../../../auth';
import { SharedDialog } from '../../../common/SharedDialog';
import { navigate } from '../../../common/navigate';
import { PageSpinner } from '../../../common/PageSpinner';
import { UI_KEYS } from '../../../common/uiKeys';
import { useSurveyList } from './hooks/useSurveyList';
import { surveysApi } from './api/surveysApi';
import { SurveyEditorDialog } from './SurveyEditorDialog';
import { DeleteSurveyDialog } from './DeleteSurveyDialog';
import { ReactivateSurveyDialog } from './ReactivateSurveyDialog';
import type { SurveyAdminRecord } from './types';

interface SurveyTableRow extends SurveyAdminRecord {
  [key: string]: unknown;
}

const SURVEY_KEYS = UI_KEYS.ADMIN_SURVEYS;

const SearchInput = styled('input', {
  width: '100%',
  minWidth: '12rem',
  height: '2.5rem',
  borderRadius: '8px',
  border: '1px solid #b6c9d9',
  padding: '0 0.75rem',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const Page = styled('section', {
  padding: '0',
  display: 'grid',
  gap: '1rem',
});

const PageHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  flexWrap: 'wrap',
});

const TableWrapper = styled('div', {
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  padding: '0',
  backgroundColor: '$white',
  '& .abyss-data-table-root': { width: '100%', minWidth: 0 },
  '& table': { width: '100%', tableLayout: 'fixed' },
  '& th, & td': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
  '& .abyss-data-table-scroll': { overflowX: 'hidden', overflowY: 'hidden' },
});

const ActionGroup = styled('div', {
  display: 'flex',
  gap: '0.125rem',
  alignItems: 'center',
  justifyContent: 'flex-end',
  whiteSpace: 'nowrap',
});

const IconActionButton = styled(Button, {
  minWidth: '32px',
  width: '32px',
  height: '32px',
  padding: '0.25rem',
});

const FilterRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const SearchFieldWrapper = styled('div', {
  minWidth: '280px',
  flex: '1 1 320px',
});

const Badge = styled('span', {
  display: 'inline-block',
  padding: '0.15rem 0.5rem',
  borderRadius: '999px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: '#d1f0e0',
  color: '#1a7a45',
  variants: {
    inactive: {
      true: {
        backgroundColor: '#fde8e8',
        color: '#a02020',
      },
    },
  },
});

const FilterControlGroup = styled('div', {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const FilterLabel = styled('label', {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#333',
  whiteSpace: 'nowrap',
});

const FilterButtonGroup = styled('div', {
  display: 'flex',
  gap: '0.25rem',
  alignItems: 'center',
  borderRadius: '8px',
  border: '1px solid #b6c9d9',
  backgroundColor: '#f5f8fb',
  padding: '0.25rem',
});

const FilterButton = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  color: '#666',
  transition: 'all 150ms ease',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#e8ecf1',
  },
  '&[data-active=true]': {
    backgroundColor: '#345570',
    color: '#fff',
  },
});

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const dateStr = value.substring(0, 10);
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function isBlank(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim().length === 0;
}

function isInactivePlaceholderSurvey(row: SurveyAdminRecord): boolean {
  return (
    !row.active &&
    isBlank(row.name) &&
    isBlank(row.description) &&
    isBlank(row.startDate) &&
    isBlank(row.endDate)
  );
}

export const SurveyListPage = () => {
  const [filterMode, setFilterMode] = React.useState<
    'active' | 'inactive' | 'all'
  >('active');
  const { surveys, isLoading, error, refresh } = useSurveyList(filterMode);
  const { logout } = useAuth();

  const [searchDraft, setSearchDraft] = React.useState('');
  const [search, setSearch] = React.useState('');

  // Editor dialog state
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingSurvey, setEditingSurvey] =
    React.useState<SurveyAdminRecord | null>(null);
  const [, setEditLoading] = React.useState(false);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] =
    React.useState<SurveyAdminRecord | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Reactivate dialog state
  const [reactivateTarget, setReactivateTarget] =
    React.useState<SurveyAdminRecord | null>(null);

  const reactivateDialogSurvey = React.useMemo(() => {
    return reactivateTarget
      ? {
          surveyId: reactivateTarget.surveyId,
          name: reactivateTarget.name?.trim() || 'Untitled RFI',
        }
      : null;
  }, [reactivateTarget]);

  // Debounced search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      return setSearch(searchDraft);
    }, 500);
    return () => {
      return clearTimeout(timer);
    };
  }, [searchDraft]);

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return surveys;
    return surveys.filter((s) => {
      return (s.name ?? '').toLowerCase().includes(q);
    });
  }, [surveys, search]);

  const tableRows = React.useMemo<SurveyTableRow[]>(() => {
    return filtered.map((s) => {
      return { ...s };
    });
  }, [filtered]);

  const columns = React.useMemo(() => {
    return [
      createColumn<SurveyTableRow>({
        id: 'name',
        accessorKey: 'name',
        header: 'RFI Name',
        size: 200,
        minSize: 150,
        enableSorting: true,
        cell: (context: { row: { original: SurveyTableRow } }) => {
          return (
            <button
              type="button"
              style={{
                appearance: 'none',
                border: 'none',
                background: 'none',
                padding: 0,
                color: 'inherit',
                font: 'inherit',
                fontWeight: 600,
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onClick={() => {
                return navigate(
                  `/admin/questions?surveyId=${context.row.original.surveyId}`
                );
              }}
            >
              {context.row.original.name?.trim() || 'Untitled RFI'}
            </button>
          );
        },
      }),
      createColumn<SurveyTableRow>({
        id: 'description',
        accessorKey: 'description',
        header: 'RFI Description',
        size: 250,
        minSize: 150,
        enableSorting: true,
        cell: (context: { row: { original: SurveyTableRow } }) => {
          return context.row.original.description ?? '—';
        },
      }),
      createColumn<SurveyTableRow>({
        id: 'active',
        accessorKey: 'active',
        header: 'Status',
        size: 100,
        minSize: 80,
        enableSorting: true,
        cell: (context: { row: { original: SurveyTableRow } }) => {
          return (
            <Badge inactive={!context.row.original.active}>
              {context.row.original.active ? 'Active' : 'Inactive'}
            </Badge>
          );
        },
      }),
      createColumn<SurveyTableRow>({
        id: 'startDate',
        accessorKey: 'startDate',
        header: 'Start Date',
        size: 130,
        minSize: 100,
        enableSorting: true,
        cell: (context: { row: { original: SurveyTableRow } }) => {
          return formatDate(context.row.original.startDate);
        },
      }),
      createColumn<SurveyTableRow>({
        id: 'endDate',
        accessorKey: 'endDate',
        header: 'End Date',
        size: 130,
        minSize: 100,
        enableSorting: true,
        cell: (context: { row: { original: SurveyTableRow } }) => {
          return formatDate(context.row.original.endDate);
        },
      }),
      createColumn<SurveyTableRow>({
        id: 'actions',
        accessorKey: 'surveyId',
        header: 'Actions',
        size: 100,
        minSize: 80,
        enableSorting: false,
        cell: (context: { row: { original: SurveyTableRow } }) => {
          const row = context.row.original;
          return (
            <ActionGroup>
              <Tooltip
                content="Edit RFI"
                position="top"
                variant="gray"
                delayDuration={200}
              >
                <IconActionButton
                  variant="text"
                  size="sm"
                  aria-label="Edit RFI"
                  title="Edit RFI"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleEdit(row);
                  }}
                >
                  <IconSymbol icon="edit" size={16} />
                </IconActionButton>
              </Tooltip>
              <Tooltip
                content="Delete RFI"
                position="top"
                variant="gray"
                delayDuration={200}
              >
                <IconActionButton
                  variant="text"
                  size="sm"
                  aria-label="Delete RFI"
                  title="Delete RFI"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setDeleteTarget(row);
                  }}
                >
                  <IconSymbol icon="delete" size={16} />
                </IconActionButton>
              </Tooltip>
              {!row.active && (
                <Tooltip
                  content="Reactivate RFI"
                  position="top"
                  variant="gray"
                  delayDuration={200}
                >
                  <IconActionButton
                    variant="text"
                    size="sm"
                    aria-label="Reactivate RFI"
                    title="Reactivate RFI"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setReactivateTarget(row);
                    }}
                  >
                    <IconSymbol icon="replay" size={16} />
                  </IconActionButton>
                </Tooltip>
              )}
            </ActionGroup>
          );
        },
      }),
    ];
  }, []);

  const tableState = useDataTable<SurveyTableRow>({
    initialData: tableRows,
    initialColumns: columns,
    rowIdKey: 'surveyId',
    tableConfig: { enableSorting: true },
    paginationConfig: { enablePagination: true, pageSizeOptions: [10, 20, 50] },
  });

  React.useEffect(() => {
    tableState.setData(tableRows, true);

    const pagination = tableState.tableInstance.getState().pagination;
    const pageSize = pagination.pageSize;
    const maxPageIndex = Math.max(
      0,
      Math.ceil(tableRows.length / pageSize) - 1
    );
    if (pagination.pageIndex > maxPageIndex) {
      tableState.tableInstance.setPageIndex(maxPageIndex);
    }
  }, [tableRows, tableState]);

  const handleEdit = async (row: SurveyAdminRecord) => {
    setEditLoading(true);
    try {
      const res = await surveysApi.getSurvey(row.surveyId);
      setEditingSurvey(res.survey);
      setEditorOpen(true);
    } catch {
      setEditingSurvey(row);
      setEditorOpen(true);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await surveysApi.deleteSurvey(deleteTarget.surveyId);
      refresh();
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const isAuthError =
    !!error && (error as Error & { status?: number }).status === 401;

  if (isAuthError) {
    return (
      <Page>
        <SharedDialog
          mode="error"
          isOpen={true}
          onClose={() => {}}
          title="Session expired"
          description="Your session has expired or is no longer valid. Please sign in again to continue."
          primaryAction={{ label: 'Sign in again', onClick: logout }}
          dismissible={false}
        />
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Text style={{ color: '#c0392b' }}>
          Failed to load surveys: {error.message}
        </Text>
      </Page>
    );
  }

  return (
    <Page
      id={SURVEY_KEYS.DOM.PAGE}
      data-section-key={SURVEY_KEYS.SECTIONS.PAGE}
    >
      <PageHeader>
        <Heading level={1} style={{ margin: 0, fontSize: '1.4rem' }}>
          RFI Surveys
        </Heading>
        <Button
          data-control-key={SURVEY_KEYS.CONTROLS.ADD_BUTTON}
          onClick={() => {
            setEditingSurvey(null);
            setEditorOpen(true);
          }}
        >
          <IconSymbol icon="add" size={16} /> Add RFI
        </Button>
      </PageHeader>

      <FilterRow
        id={SURVEY_KEYS.DOM.FILTERS}
        data-section-key={SURVEY_KEYS.SECTIONS.FILTERS}
      >
        <SearchFieldWrapper>
          <SearchInput
            id={SURVEY_KEYS.DOM.SEARCH_INPUT}
            data-control-key={SURVEY_KEYS.CONTROLS.SEARCH_INPUT}
            type="text"
            placeholder="Search RFI Name..."
            value={searchDraft}
            onChange={(e) => {
              return setSearchDraft(e.target.value);
            }}
            aria-label="Search RFI Name"
          />
        </SearchFieldWrapper>
        <FilterControlGroup>
          <FilterLabel htmlFor={SURVEY_KEYS.DOM.FILTER_GROUP}>
            Filter by Status:
          </FilterLabel>
          <FilterButtonGroup
            id={SURVEY_KEYS.DOM.FILTER_GROUP}
            data-control-key={SURVEY_KEYS.CONTROLS.FILTER_GROUP}
            role="radiogroup"
            aria-label="Filter surveys by status"
          >
            <FilterButton
              type="button"
              data-active={filterMode === 'active'}
              onClick={() => {
                return setFilterMode('active');
              }}
              aria-pressed={filterMode === 'active'}
              aria-label="Show active surveys only"
            >
              Active Only
            </FilterButton>
            <FilterButton
              type="button"
              data-active={filterMode === 'all'}
              onClick={() => {
                return setFilterMode('all');
              }}
              aria-pressed={filterMode === 'all'}
              aria-label="Show all surveys"
            >
              All Surveys
            </FilterButton>
            <FilterButton
              type="button"
              data-active={filterMode === 'inactive'}
              onClick={() => {
                return setFilterMode('inactive');
              }}
              aria-pressed={filterMode === 'inactive'}
              aria-label="Show inactive surveys only"
            >
              Inactive Only
            </FilterButton>
          </FilterButtonGroup>
        </FilterControlGroup>
      </FilterRow>

      {isLoading ? (
        <PageSpinner label="Loading surveys" />
      ) : filtered.length === 0 ? (
        <Text style={{ color: '#666', fontStyle: 'italic' }}>
          No surveys found.
        </Text>
      ) : (
        <TableWrapper
          id={SURVEY_KEYS.DOM.TABLE}
          data-section-key={SURVEY_KEYS.SECTIONS.TABLE}
        >
          <DataTable<SurveyTableRow>
            title={`Surveys (${filtered.length})`}
            hideHeader
            tableState={tableState}
          >
            <DataTable.Pagination />
            <DataTable.Table />
          </DataTable>
        </TableWrapper>
      )}

      <SurveyEditorDialog
        isOpen={editorOpen}
        survey={editingSurvey}
        onClose={() => {
          return setEditorOpen(false);
        }}
        onSaved={refresh}
      />

      <DeleteSurveyDialog
        surveyName={deleteTarget?.name?.trim() || 'this RFI'}
        isPermanentDelete={
          deleteTarget ? isInactivePlaceholderSurvey(deleteTarget) : false
        }
        isOpen={!!deleteTarget}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          return setDeleteTarget(null);
        }}
      />

      <ReactivateSurveyDialog
        isOpen={!!reactivateTarget}
        survey={reactivateDialogSurvey}
        onClose={() => {
          return setReactivateTarget(null);
        }}
        onReactivated={refresh}
      />
    </Page>
  );
};
